import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { Loader2, RotateCcw, AlertTriangle, Info } from "lucide-react";
import { quoteHeadingNamesSchema, type QuoteHeadingNames } from "@workspace/api-zod/quote-headings";
import { useQuoteHeadingPreferences, usePatchQuoteHeadingName } from "@/hooks/use-quote-heading-preferences";

export const HEADING_NAME_MAX = 60;

/** Validate drafts with the shared schema; returns cleaned names or per-key errors. */
export function validateHeadingDrafts(drafts: Record<string, string>):
  { ok: true; names: QuoteHeadingNames } | { ok: false; errors: Record<string, string> } {
  const raw: Record<string, string> = {};
  for (const [k, v] of Object.entries(drafts)) if (v.trim()) raw[k] = v.trim();
  const parsed = quoteHeadingNamesSchema.safeParse(raw);
  if (parsed.success) return { ok: true, names: parsed.data };
  const errors: Record<string, string> = {};
  for (const issue of parsed.error.issues) {
    const key = String(issue.path[0] ?? "");
    if (!key || errors[key]) continue;
    errors[key] = issue.code === "too_big" ? `Keep it to ${HEADING_NAME_MAX} characters or fewer.` : issue.message;
  }
  return { ok: false, errors };
}

export type NavigationHeadingEntry = { key: string; label: string; group: string; quoteKey?: string };

/** Current display name: nav key first, then legacy quote alias, then default. */
export function resolveEntryName(entry: NavigationHeadingEntry, names: QuoteHeadingNames | null | undefined): string {
  const own = names?.[entry.key]?.trim();
  if (own) return own;
  const legacy = entry.quoteKey ? names?.[entry.quoteKey]?.trim() : "";
  return legacy || entry.label;
}

/** Strip "409: " status prefix and "(ref: ...)" suffix from ApiError messages. */
function cleanServerError(err: unknown): string {
  const e = err as { status?: number; data?: { error?: unknown } } | null;
  if (e && typeof e.status === "number") {
    if (e.status < 500 && typeof e.data?.error === "string" && e.data.error.trim()) return e.data.error.trim();
    return "We couldn't save that name. Please try again.";
  }
  const raw = err instanceof Error ? err.message : String(err ?? "");
  const msg = raw.replace(/^\d{3}:\s*/, "").replace(/\s*\(ref:[^)]*\)\s*$/, "").trim();
  if (!msg || msg.startsWith("{") || msg.startsWith("<")) return "We couldn't save that name. Please try again.";
  return msg;
}

const MARKUP = /[<>]/;

export function NavigationHeadingNameDialog({
  entry,
  onOpenChange,
}: {
  entry: NavigationHeadingEntry | null;
  onOpenChange: (open: boolean) => void;
}) {
  const { toast } = useToast();
  const prefs = useQuoteHeadingPreferences();
  const patch = usePatchQuoteHeadingName();
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");
  const loadToken = useRef(0);
  const refetchRef = useRef(prefs.refetch);
  refetchRef.current = prefs.refetch;
  const open = !!entry;
  const entryKey = entry?.key ?? null;
  const accountId = prefs.accountId;
  const entryRef = useRef(entry);
  entryRef.current = entry;

  async function loadFresh() {
    const token = ++loadToken.current;
    const current = entryRef.current;
    if (!current) return;
    setStatus("loading");
    setError(null);
    try {
      const res = await refetchRef.current();
      if (token !== loadToken.current) return; // stale: entry/account changed or closed
      if (res.isError || !res.data) { setStatus("error"); return; }
      setDraft(resolveEntryName(current, res.data.names ?? {}));
      setStatus("ready");
    } catch {
      if (token === loadToken.current) setStatus("error");
    }
  }

  const saveToken = useRef(0);
  useEffect(() => {
    // Any open/entry/account transition orphans in-flight loads AND saves.
    saveToken.current++;
    if (open) void loadFresh();
    else loadToken.current++;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, entryKey, accountId]);
  useEffect(() => () => { loadToken.current++; saveToken.current++; }, []);

  if (!entry) {
    return null;
  }

  const trimmed = draft.trim();
  const isDefault = !trimmed || trimmed === entry.label;
  const busy = status === "loading";
  const saving = patch.isPending;

  function validateLocal(): string | null {
    if (trimmed.length > HEADING_NAME_MAX) return `Keep it to ${HEADING_NAME_MAX} characters or fewer.`;
    if (MARKUP.test(trimmed)) return "Use plain text only, without < or > characters.";
    return null;
  }

  function handleSave() {
    if (!entry) return;
    const local = validateLocal();
    if (local) { setError(local); return; }
    const value = isDefault ? "" : trimmed;
    const token = ++saveToken.current;
    const lt = loadToken.current;
    const savingKey = entry.key;
    const live = () => token === saveToken.current && lt === loadToken.current && entryRef.current?.key === savingKey;
    patch.mutate({ key: savingKey, value }, {
      onSuccess: () => {
        if (!live()) return;
        toast({ title: value ? "Name saved" : "Name reset to default" });
        onOpenChange(false);
      },
      onError: (err) => {
        if (!live()) return;
        setError(cleanServerError(err)); // draft preserved
      },
    });
  }

  function handleCancel() {
    saveToken.current++;
    onOpenChange(false);
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o) handleCancel(); }}>
      <DialogContent className="max-w-md" data-testid="dialog-heading-name">
        <DialogHeader>
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground" data-testid="text-heading-group">{entry.group}</p>
          <DialogTitle className="break-words [overflow-wrap:anywhere]">Rename category</DialogTitle>
          <DialogDescription>
            Default name: <span className="font-medium text-foreground break-words" data-testid="text-heading-default">{entry.label}</span>
          </DialogDescription>
        </DialogHeader>

        {busy ? (
          <div className="space-y-2" data-testid="status-heading-name-loading">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : status === "error" ? (
          <div className="flex flex-col items-center gap-3 py-6 text-center" data-testid="status-heading-name-error">
            <AlertTriangle className="h-6 w-6 text-destructive" />
            <p className="text-sm text-muted-foreground">We couldn't load your saved names.</p>
            <Button variant="outline" onClick={() => void loadFresh()} data-testid="button-retry-heading-name">Try again</Button>
          </div>
        ) : (
          <form
            className="space-y-2"
            onSubmit={(e) => { e.preventDefault(); handleSave(); }}
          >
            <label htmlFor="heading-name-input" className="text-sm font-medium">Name shown</label>
            <Input
              id="heading-name-input"
              autoFocus
              value={draft}
              maxLength={HEADING_NAME_MAX}
              placeholder={entry.label}
              onChange={(e) => { setDraft(e.target.value); setError(null); }}
              aria-invalid={!!error}
              aria-describedby="heading-name-help"
              data-testid="input-heading-name"
            />
            <div id="heading-name-help" className="flex justify-between gap-3 text-xs text-muted-foreground">
              <span>Leave blank to use the default.</span>
              <span className="tabular-nums">{draft.length}/{HEADING_NAME_MAX}</span>
            </div>
            {error && (
              <p role="alert" className="text-sm text-destructive" data-testid="error-heading-name">{error}</p>
            )}
            {entry.quoteKey ? (
              <p className="text-xs text-muted-foreground" data-testid="text-heading-scope-quote">
                Also used as the heading on new quotes. Existing quotes and prices are unchanged.
              </p>
            ) : (
              <p className="flex gap-2 rounded-md bg-muted/60 p-2.5 text-xs text-muted-foreground" data-testid="text-heading-scope-library">
                <Info className="h-4 w-4 shrink-0 mt-px" aria-hidden="true" />
                Only changes how this appears in your Cost Library. It won't create or rename a heading on quotes.
              </p>
            )}
          </form>
        )}

        <DialogFooter className="gap-2 sm:justify-between">
          <Button
            variant="ghost"
            disabled={busy || status === "error" || saving || isDefault}
            onClick={() => { setDraft(entry.label); setError(null); }}
            data-testid="button-reset-heading-name"
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            Reset to default
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleCancel} data-testid="button-cancel-heading-name">Cancel</Button>
            <Button onClick={handleSave} disabled={busy || status === "error" || saving} data-testid="button-save-heading-name">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
